import { useState, useEffect, useRef } from "react"
import { useParams, useLocation } from "react-router"
import { useDispatch, useSelector } from "react-redux"
import Button from "react-bootstrap/Button"
import Spinner from "react-bootstrap/Spinner"
import { ListGroup } from "react-bootstrap"
import { useDropzone } from "react-dropzone"
import { FiFolder, FiFile, FiInfo, FiCheck } from "react-icons/fi"
import { request } from "utils/request"
import UploadDisplay from "./UploadDisplay"
import { Loading, Popup } from "components"
import { locations, repositories as repositoriesState, message } from "state_management"
import useT from "utils/hooks/useT"

import "./index.scss"
import { updateHelpRepository } from "state_management/librarian"

export default function RepositoryScreen() {
  const { repositoryId } = useParams()
  const location = useLocation()
  const dispatch = useDispatch()
  const { t } = useT("messages")
  const { t: tc } = useT("common")

  const [files, setFiles] = useState([])
  const [fileUploadStatus, setFileUploadStatus] = useState({ rejectedFiles: [], failedFiles: [] })
  const [resultMessage, setResultMessage] = useState(null)

  const companyId = useSelector((state) => state.auth.companyId)
  const { isLoadingGlobalRepositories, globalRepositories, hasFetched, repositories } = useSelector(
    (state) => state.repositories
  )
  const [progress, setProgress] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  const repository = [...repositories, ...globalRepositories].find((repository) => repository.id === repositoryId)
  const showProcessing = !repository.isProcessing && repository.unprocessedFiles
  const [showErrors, setShowErrors] = useState(false)

  const isCustomerAdmin = useSelector((state) => state.auth.role === "customer_admin")
  const isGlobal = location.pathname.includes("/customers/global-repositories/")
  const isFirstRef = useRef(true)

  useEffect(() => {
    if (isFirstRef.current) {
      isFirstRef.current = false
      dispatch(repositoriesState.fetchRepository(repositoryId))
    }
  }, [dispatch, repositoryId])

  if (!hasFetched && !repository) {
    dispatch(locations.fetch(companyId))
    dispatch(repositoriesState.fetch())
    if (isCustomerAdmin) {
      dispatch(repositoriesState.fetchGlobal())
    }
    return <Loading />
  }

  if (!repository || isLoadingGlobalRepositories) {
    return <Loading />
  }

  const isProcessing = repository.isProcessing
  const hasUnprocessedFiles = repository.unprocessedFiles
  const isDoneProcessing = !isProcessing && repository.isProcessed
  if (repository.name === "Plannertech Help") {
    return (
      <div className="help-repository">
        <div className="header-container">
          <p className="repository-company-headline">{repository.name}</p>
        </div>
        <div className="update-button">
          <Button onClick={() =>
            dispatch(updateHelpRepository(repositoryId))
            .then(dispatch(message.success(t("adhoc.helpPageUpdated"))))}
            disabled={isLoading}
            variant="success">
            {isLoading ? <Spinner animation="grow" size="sm" /> : ""}
            {isLoading ? ` ${progress}%` : tc("updateHelpPage")}
          </Button>
        </div>
      </div>
    )
  }
  return (
    <div className="upload-container">
      {showErrors && (
        <Popup
          onCancel={() => setShowErrors(false)}
          title={tc("errors")}
        >
          <ul className="error-list">
            {repository.processingErrors.length === 0 ? <h4>{tc("noErrorsToDisplay")}</h4>
            : <h4>{tc("contactArnvindSupport")}</h4>}
            {repository.processingErrors.map((error, index) => (
              <li key={index}>
                <strong>{index + 1}.</strong> {error}
              </li>
            ))}
          </ul>
        </Popup>
      )}
      <div className="header-container">
        <p className="repository-company-headline">{repository.name}</p>
        <Button className="show-errors-button" onClick={() => setShowErrors(true)} variant="primary">{tc("showErrors")}</Button>
      </div>

      <UploadDropzone files={files} onSubmit={setFiles} fileUploadStatus={fileUploadStatus} setFileUploadStatus={setFileUploadStatus} tc={tc} />
      <div className="repository-buttons">
        <div className="repository-upload-buttons">
          <UploadButton files={files} setFiles={setFiles} setResultMessage={setResultMessage} progress={progress} setProgress={setProgress} isLoading={isLoading} setIsLoading={setIsLoading} fileUploadStatus={fileUploadStatus} setFileUploadStatus={setFileUploadStatus} repositoryId={repositoryId} tc={tc} />
          {(fileUploadStatus.rejectedFiles.length > 0 || files.length > 0) && (
            <Button
              variant="secondary"
              onClick={() => {
                setFiles([])
                setFileUploadStatus({ rejectedFiles: [], failedFiles: [] })
              }}
            >
              {tc("clear")}
            </Button>
          )}
        </div>

        {(isCustomerAdmin || showProcessing) && (
          <div className="repository-processing-button-container">
            {isDoneProcessing && !repository.unprocessedFiles && <FiCheck className="repository-check-icon" title={tc("repositoryIsProcessed")} />}
            <div className="repository-processing-button">
              <Button
                variant="success"
                disabled={!isCustomerAdmin && (isProcessing || !repository.unprocessedFiles)}
                onClick={() => {
                  dispatch(repositoriesState.startProcessing(repositoryId, isGlobal))
                  dispatch(message.success(t("adhoc.processingStarted")))
                }}
              >
                {isProcessing ? <Spinner animation="border" size="sm" /> : ""}
                {isProcessing ? ` ${tc("processingInProgress")}` : repository.isProcessed && !repository.unprocessedFiles ? tc("processed") : tc("process")}
              </Button>
            </div>
            <div
              className="repository-process-info-text"
              title={`${
                isProcessing
                  ? tc("repositoryProcessingInProgressInfo")
                  : hasUnprocessedFiles
                  ? tc("repositoryHasUnprocessedFilesInfo")
                  : isDoneProcessing
                  ? tc("allFilesProcessed")
                  : tc("startProcessingInfo")
              }`}
            >
              <FiInfo />
            </div>
          </div>
        )}
      </div>

      {fileUploadStatus.rejectedFiles.length > 0 && (
        <div className="repository-upload-rejected-files">
          <h4>
            {tc("rejectedFilesIncompatible")}
            <span className="repository-config-info">
              <FiInfo title={tc("acceptedFileFormats")} />
            </span>
          </h4>

          <div className="repository-upload-rejected-files-listgroup">
            <ListGroup>
              {fileUploadStatus.rejectedFiles.map((file) => (
                <ListGroup.Item variant="danger" key={file.name}>
                  {file.name}
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        </div>
      )}
      {resultMessage && (
        <Popup
          onCancel={() => {
            setResultMessage(null)
            setFileUploadStatus({ rejectedFiles: [], failedFiles: [] })
            setFiles([]) // Clear the files
          }}
          title={tc("uploadFinished")}
          center
          className="repository-upload-popup"
        >
          <UploadDisplay
            files={files}
            failedFiles={fileUploadStatus.failedFiles}
            rejectedFiles={fileUploadStatus.rejectedFiles}
          />
        </Popup>
      )}
    </div>
  )
}

function UploadDropzone({ files, onSubmit, fileUploadStatus, setFileUploadStatus, tc }) {
  const uniqueDirectories = [
    ...new Set(
      files.map((file) => {
        const curFile = file.path.split("/")
        if (curFile.length > 1) {
          return curFile[1]
        } else {
          return curFile[0]
        }
      })
    ),
  ]

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles, rejectedFiles) => {
      // Check file types of all accepted files
      acceptedFiles = acceptedFiles.filter((file) => {
        const fileType = file.type
        if (
          fileType !== "application/pdf" &&
          fileType !== "application/msword" &&
          fileType !== "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          //fileType !== "text/plain" &&
        ) {
          rejectedFiles.push(file)
          return false
        }
        return true
      })

      let foundIndexes = []
      var trimmedAcceptedFiles = acceptedFiles
      let trimmedFiles = files.map((a) => {
        let idx = acceptedFiles.findIndex((b) => b.name === a.name)
        if (idx === -1) {
          return a
        } else {
          foundIndexes.push(idx)
          return acceptedFiles[idx]
        }
      })
      foundIndexes = foundIndexes.sort((a, b) => {
        return a < b ? -1 : a > b ? 1 : 0
      })
      for (var i = foundIndexes.length - 1; i >= 0; i--) {
        trimmedAcceptedFiles.splice(foundIndexes[i], 1)
      }
      const updatedFiles = { acceptedFiles: [...trimmedFiles, ...trimmedAcceptedFiles] }
      const rejectedFilesToAdd = rejectedFiles.filter((file1) => {
        return !fileUploadStatus.rejectedFiles.some((file2) => file2.name === file1.name)
      })

      setFileUploadStatus({
        ...fileUploadStatus,
        rejectedFiles: [...fileUploadStatus.rejectedFiles, ...rejectedFilesToAdd],
      })
      onSubmit(updatedFiles.acceptedFiles)
    },
  })

  const differentFileTypes = [".pdf", ".txt", ".docx"]
  return (
    <div {...getRootProps({ className: "repository-upload-box" })}>
      <input {...getInputProps()} />
      <div>
        {files.length ? <p>{tc("selectedFilesReadyForSubmit")}</p> : null}
        <ul>
          {files.length
            ? uniqueDirectories.map((uniqueDirectory) => (
              <li key={uniqueDirectory} className="UploadDropzone-repository-files">
                {!differentFileTypes.some((fileType) => uniqueDirectory.includes(fileType)) ? (
                  <FiFolder />
                ) : (
                  <FiFile />
                )}
                <span>{uniqueDirectory}</span>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    const newFiles = files.filter((file) => {
                      const directory = file.path.split("/")
                      if (directory.length > 1) {
                        return directory[1] !== uniqueDirectory
                      } else {
                        return !file.path.includes(uniqueDirectory)
                      }
                    })
                    onSubmit(newFiles)
                  }}
                  className="UploadDropzone-remove-file"
                  title={tc("delete")}
                  aria-label={tc("close")}
                  type="button"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </li>
            ))
            : null}{" "}
        </ul>
      </div>
      <p>
        {isDragActive
          ? tc("dropFoldersHere")
          : files.length
          ? tc("clickToSelectOtherFiles")
          : tc("dragAndDropFolders")}
      </p>
    </div>
  )
}

function UploadButton({ files, setFiles: _setFiles, setResultMessage, progress, setProgress, isLoading, setIsLoading, fileUploadStatus, setFileUploadStatus, repositoryId, tc }) {
  const dispatch = useDispatch()
  const { t: tMsg } = useT("messages")

  const uploadFilesToS3 = async (files, presignedData) => {
    let failedFiles = []
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      const url = presignedData[i]["url"]
      const data = presignedData[i]["fields"]

      const formData = new FormData()

      const newProgress = Math.round(((i + 1) / files.length) * 100)
      if (newProgress > progress) {
        setProgress(newProgress)
      }

      Object.keys(data).forEach((key) => {
        formData.append(key, data[key])
      })
      formData.append("file", file)

      const uploadResponse = await fetch(url, { method: "POST", body: formData })

      if (!uploadResponse.ok) {
        failedFiles.push(tc("uploadFailedForFile", { path: file.path }))
      }
    }
    return failedFiles
  }

  async function getPresignedPostData() {
    const input = {
      filePaths: files.map((file) => file.path),
    }

    try {
      const res = await request("POST", `/librarian/bucket/get-upload-url/${repositoryId}`, { input })
      return res.presignedPosts
    } catch (e) {
      dispatch(message.error(tMsg("adhoc.anErrorOccurred"), e))
    }
  }

  const onSubmit = async () => {
    if (isLoading) return
    if (files.length === 0) {
      dispatch(message.warning(tMsg("adhoc.selectFileBeforeSubmitting")))
      return
    }

    setIsLoading(true)
    let failedFiles = []
    try {
      const presignedData = await getPresignedPostData()
      failedFiles = await uploadFilesToS3(files, presignedData)
      dispatch(repositoriesState.fetchRepository(repositoryId))

      setResultMessage(tc("filesUploadedSuccessfully"))
    } catch {
      setResultMessage(tc("fileUploadError"))
    }
    setFileUploadStatus({ ...fileUploadStatus, failedFiles: failedFiles })
    setIsLoading(false)
    setProgress(0)
  }

  return (
    <>
      <Button onClick={onSubmit} disabled={isLoading} variant="success">
        {isLoading ? <Spinner animation="grow" size="sm" /> : ""}
        {isLoading ? ` ${progress}%` : tc("submit")}
      </Button>
    </>
  )
}
